import axios from 'axios'
import MercureListener from './MecureListener'
import authService, { GRANT } from './AuthService'
import config from './../config'

const { API_URL, SITE_URL, API_TIMEOUT } = config.env

const MAX_REQUEST_RETRIES = 1

/**
 * ApiService class
 */
class ApiService {
  /**
   * Constructor
   */
  constructor () {
    this.axios = null
    this.createApi()
  }

  /**
   * @param uri
   * @param params
   * @param config
   * @param onReceiveMessage
   */
  subscribe (uri, params, config, onReceiveMessage) {
    config = config || {}
    if (typeof config.transformResponse !== 'object') {
      config.transformResponse = []
    }
    config.transformResponse.push(function () {
      return (data) => {
        const response = JSON.parse(data)
        response.mercureListener = new MercureListener(response, onReceiveMessage || null)
        return response
      }
    }())
    return this.get(uri, params, config)
  }

  /**
   *
   */
  createApi () {
    delete this.axios
    this.axios = axios.create(this.getBaseSettings())
  }

  /**
   *
   */
  getBaseSettings () {
    return {
      timeout: API_TIMEOUT || 20000
      // headers: {
      //   'X-Source-system': 'OrderFlow',
      // }
    }
  }

  /**
   * @param config
   * @returns {Promise<*>}
   */
  async doRequest (config) {
    config = {
      api_url: null,
      useSiteEndpoint: false,
      ...config
    }

    if (config.retryCount === undefined) {
      config.retryCount = 0
    }

    const useSiteEndpoint = !!config.useSiteEndpoint

    let baseUrl
    if (config.api_url) {
      baseUrl = new URL(config.api_url)
      delete config.api_url
    } else {
      baseUrl = new URL(useSiteEndpoint === false ? API_URL : SITE_URL)
    }

    let endpoint = config.url
    if (endpoint.startsWith(baseUrl.pathname)) {
      endpoint = endpoint.replace(baseUrl.pathname, '')
    }

    if (config.url.substr(0, 4) !== 'http') {
      config.url = baseUrl + endpoint
    }

    if (useSiteEndpoint === false) {
      if (config.client_grant === undefined) {
        const token = await authService.getAccessToken()

        if (token !== null) {
          config.headers = {
            ...config.headers,
            Authorization: 'Bearer ' + token
          }
        }
      } else if (config.client_grant === GRANT.ORIGIN && config.url.indexOf('client_id') === -1) {
        if (config.method === 'get') {
          config.params = {
            ...config.params,
            client_id: config.client_id
          }
        } else if (config.client_id) {
          config.data = {
            ...config.data,
            client_id: config.client_id
          }
        }
      }
    }

    return this.axios.request(config)
      .catch(async (error) => {
        if (
          typeof error.response.data.error_description !== 'undefined' &&
          error.response.data.error_description === 'The refresh token is invalid.'
        ) {
          authService.logout()
          throw error
        }

        if (
          error.response.status === 401 &&
          config.retryCount < MAX_REQUEST_RETRIES
        ) {
          if (authService.isRefreshing === true) {
            await authService.waitForTokenRefresh()
          } else {
            await authService.refreshToken()
          }

          config.retryCount++
          return this.doRequest(config)
        }

        throw error
      })
  }

  /**
   * Get single entity
   *
   * @param url
   * @param params
   * @param config
   * @returns {Promise<*>}
   */
  get (url = '', params = {}, config = {}) {
    return this.doRequest({
      ...(config || {}),
      url,
      method: 'get',
      params: (params || {})
    })
  }

  /**
   * Delete single entity
   *
   * @param url
   * @param config
   * @returns {Promise<*>}
   */
  delete (url = '', config = {}) {
    return this.doRequest({
      ...(config || {}),
      url,
      method: 'delete'
    })
  }

  /**
   * Update single entity
   *
   * @param url
   * @param data
   * @param config
   * @returns {Promise<*>}
   */
  post (url = '', data = {}, config = {}) {
    return this.doRequest({
      ...(config || {}),
      url,
      method: 'post',
      data: (data || {})
    })
  }

  /**
   * @param url
   * @param data
   * @param config
   * @returns {Promise<*>}
   */
  put (url = '', data = {}, config = {}) {
    return this.doRequest({
      ...(config || {}),
      url,
      method: 'put',
      data: (data || {})
    })
  }

  /**
   * @param url
   * @param data
   * @param config
   * @returns {Promise<*>}
   */
  patch (url = '', data = {}, config = {}) {
    return this.doRequest({
      ...(config || {}),
      url,
      method: 'patch',
      data: (data || {})
    })
  }
}

export default new ApiService()
